import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Alert } from 'reactstrap';
import styled from 'styled-components';
import { getPrivateMoney, updatePrivateMoney } from '~/javascripts/admin/actions/private-moneys';
import { buildApiFormErrors } from '~/javascripts/admin/utils/apiErrors';
import { Button } from '../../atoms/Buttons';
import FormErrorMessage from '../../atoms/FormErrorMessage';
import Grid from '../../atoms/Grid';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import Required from '../../atoms/Required';
import TextBox from '../../atoms/TextBox';
import BreadCrumb from '../../molecules/BreadCrumb';
import PrivateMoneySearchForm, { PrivateMoneySearchFields } from './PrivateMoneySearchForm';

const FormWrapper = styled.div``;

const FormGrid = styled(Grid.Container)`
  margin-top: 24px;

  ${Button} {
    margin: 24px 0;
  }

  ${Label} {
    margin-bottom: 0;
    line-height: 32px;
  }
`;

type RenamePrivateMoneyFields = {
  name: string;
  furigana: string;
};

type SearchedPrivateMoneyDetails = { private_money_id: string } & RenamePrivateMoneyFields;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: 'サービス管理', href: '', isCurrentPage: true },
  { value: 'マネー変更', href: '', isCurrentPage: true },
];

const getApiError = buildApiFormErrors<keyof RenamePrivateMoneyFields>({
  private_money_conflict: () => ({
    key: 'name',
    error: {
      message: 'このマネー名は既に登録されています',
    },
  }),
});

const RenamePrivateMoney = () => {
  const [searchedPrivateMoneyDetails, setSearchedPrivateMoneyDetails] =
    useState<SearchedPrivateMoneyDetails>({
      private_money_id: '',
      name: '',
      furigana: '',
    });

  const [isMoneySuccessfullyUpdated, setIsMoneySuccessfullyUpdated] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<RenamePrivateMoneyFields>();

  const getPrivateMoneyDetails = async (data: PrivateMoneySearchFields) => {
    const { private_money_id } = data;
    const res = await getPrivateMoney(private_money_id.trim());

    setSearchedPrivateMoneyDetails({
      private_money_id: res.id,
      name: res.name,
      furigana: res.furigana,
    });
  };

  const onSubmit = async (data: RenamePrivateMoneyFields) => {
    try {
      const formData = new FormData();

      formData.append('_csrf_token', window._csrf_token);
      Object.keys(data).forEach((key) => {
        if (data[key] != null) {
          formData.append(key, data[key].trim());
        }
      });

      await updatePrivateMoney(searchedPrivateMoneyDetails.private_money_id, formData);
      setIsMoneySuccessfullyUpdated(true);
      setTimeout(() => setIsMoneySuccessfullyUpdated(false), 5000);
    } catch (e) {
      const { key: errorKey, error } = getApiError(await Promise.resolve(e));
      setError(errorKey, error);
    }
  };

  return (
    <FormWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      <Headings.h5>検索条件</Headings.h5>
      <PrivateMoneySearchForm onSearch={getPrivateMoneyDetails} />
      <Headings.h5>マネー情報</Headings.h5>
      <FormGrid columnGap={32}>
        <Grid.Item startColumn={3} size={4}>
          <Label>変更前</Label>
        </Grid.Item>
        <Grid.Item size={4}>
          <Label>変更後</Label>
        </Grid.Item>

        <Grid.Item nextRow size={2}>
          <Label>
            マネー名
            <Required />
          </Label>
        </Grid.Item>
        <Grid.Item size={4}>
          <TextBox fullWidth syncValue={searchedPrivateMoneyDetails.name} disabled />
        </Grid.Item>
        <Grid.Item size={4} flexDirection="column">
          <TextBox fullWidth {...register('name', { required: 'マネー名を入力してください' })} />
          <FormErrorMessage name="name" errors={errors} />
        </Grid.Item>

        <Grid.Spacer space={24} />

        <Grid.Item size={2}>
          <Label>
            ふりがな
            <Required />
          </Label>
        </Grid.Item>
        <Grid.Item size={4}>
          <TextBox fullWidth syncValue={searchedPrivateMoneyDetails.furigana} disabled />
        </Grid.Item>
        <Grid.Item size={4} flexDirection="column">
          <TextBox
            fullWidth
            {...register('furigana', { required: 'ふりがなを入力してください' })}
          />
          <FormErrorMessage name="furigana" errors={errors} />
        </Grid.Item>

        <Grid.Item nextRow>
          <Button onClick={handleSubmit(onSubmit)}>更新</Button>
        </Grid.Item>
      </FormGrid>
      <Alert color="success" isOpen={isMoneySuccessfullyUpdated}>
        マネーの更新に成功しました
      </Alert>
    </FormWrapper>
  );
};

export default RenamePrivateMoney;
